import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE } from '@/constants';
import { ClientContainer, UserContainer } from '@/providers';
import { IPlan } from '@/components/subscription-change-modal/constant';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BILLING_ANALYTICS_EVENTS } from '@/analytics/billing';
import { trackEvent } from '@/helpers/external-tools-helper';
import teamUpgradeCover from '../../images/upgrade-modal-cover.svg';
import professionalCover from '../../images/upgrade-modal-professional.svg';
import teamLogo from '../../images/team-icon.svg';
import professionalLogo from '../../images/professional-icon.svg';

// Define plan configurations
const PLAN_CONFIGS = {
  professional: {
    title: 'Professional',
    description: 'To grow referrals and leads of business looking',
    price: 39,
    features: [
      { key: 'data-sources_other', count: 20 },
      { key: 'workspace_unlimited', bold: true },
      { key: 'users-single' },
      { key: 'analytics-dashboard' },
      { key: 'google-add-on' },
      { key: 'reporting' },
    ],
    icon: 'briefcase',
  },
  team: {
    title: 'Team',
    description: 'Perfect for growing teams and businesses',
    price: 99,
    features: [
      { key: 'data-sources_other', count: 50 },
      { key: 'workspace_unlimited' },
      { key: 'users_unlimited', bold: true },
      { key: 'analytics-dashboard' },
      { key: 'google-add-on' },
      { key: 'reporting' },
    ],
    icon: 'users',
  },
};

const usePricingPlans = () => {
  const { paymentPlans } = UserContainer.useContainer();

  const allPricingPlans: {
    month: IPlan[];
    year: IPlan[];
  } = paymentPlans ?? {
    month: [],
    year: [],
  };

  const { month: monthlyPlans, year: yearlyPlans } = allPricingPlans;

  return {
    monthlyPlans,
    yearlyPlans,
  };
};

interface TeamUpgradeDialogProps {
  open: boolean;
  onOpenChange: (openState: boolean) => void;
  planName: 'professional' | 'team';
}

const TeamUpgradeDialog = ({
  open,
  onOpenChange,
  planName = 'team',
}: TeamUpgradeDialogProps) => {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { selectedClient } = clientState;

  const planConfig = PLAN_CONFIGS[planName];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0">
        <DialogHeader className="p-6">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <i className={`ti ti-${planConfig.icon} text-yellow-400`} />
            {t(
              planName === 'team'
                ? 'plan-team-upgrade-description'
                : 'plan-professional-upgrade-description'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 p-6 pr-0 pb-4 border-t">
          <div className="space-y-6">
            <div>
              <img
                src={planName === 'team' ? teamLogo : professionalLogo}
                alt=""
              />
              <div className="text-xl font-semibold">{planConfig.title}</div>
              <div className="text-sm text-gray-500">
                {planConfig.description}
              </div>
            </div>

            <div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold">${planConfig.price}</span>
                <span className="text-gray-500 ml-1">/{t('month')}</span>
              </div>
              <div className="text-sm text-gray-500">{t('billed-yearly')}</div>
            </div>

            <div className="space-y-3">
              <div className="font-medium text-xs text-gray-500">
                {t('features')}
              </div>
              <ul className="space-y-3">
                {planConfig.features.map(feature => (
                  <li
                    key={feature.key}
                    className="flex items-center gap-2 text-xs text-gray-500"
                  >
                    <i className="ti ti-check text-blue-500" />
                    <span
                      className={`${
                        feature.bold
                          ? 'font-semibold text-black'
                          : 'font-normal'
                      }`}
                    >
                      {feature.count
                        ? t(feature.key, { count: feature.count })
                        : t(feature.key)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="w-auto h-auto overflow-hidden">
            <img
              src={planName === 'team' ? teamUpgradeCover : professionalCover}
              alt={planConfig.title}
              className="w-[500px] scale-110"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <Button intent="secondary" onClick={() => onOpenChange(false)}>
            {t('action_cancel')}
          </Button>
          <Button
            startIcon={<i className="ti ti-bolt" />}
            onClick={() => {
              trackEvent(
                BILLING_ANALYTICS_EVENTS.SUBSCRIPTION_UPGRADE_CHECKED,
                {
                  planName,
                }
              );
              navigate(
                `/ws/${selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}?plan=${planName}`
              );
              onOpenChange(false);
            }}
          >
            {t('button-view-all-plans', 'View All Plans')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeamUpgradeDialog;
